/* eslint-disable lodash/import-scope */
import { Box, ButtonMenu, ButtonMenuItem, Flex, Text, useMatchBreakpoints } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { useState, memo } from 'react'
import { useFetchPairPricesV3 } from 'state/swap/hooks'
import dynamic from 'next/dynamic'
import { PairDataTimeWindowEnum } from 'state/swap/types'
import styled from 'styled-components'
import { isNaN } from 'lodash'
import NoChartAvailable from './NoChartAvailable'
import PairPriceDisplay from '../../../../components/PairPriceDisplay'
import { getTimeWindowChange } from './utils'

const SwapLineChart = dynamic(() => import('@pancakeswap/uikit/src/components/Chart/PairPriceChart'), {
  ssr: false,
})

const StyledButtonMenu = styled(Box)`
  div {
    border: 1px solid rgba(255, 255, 255, 0.1);

    background: transparent;

    button {
      height: 28px;

      font-weight: 500;
      font-size: 14px;
      line-height: 140%;

      color: rgba(255, 255, 255, 0.6);

      background: #2e81ff;
      border-radius: 24px;
    }
  }
`

const ButtonMenuItemStyle = styled(ButtonMenuItem)`
  width: 48px;

  margin-left: 0 !important;
`

const BasicChart = ({
  token0Address,
  token1Address,
  isChartExpanded,
  inputCurrency,
  outputCurrency,
  isMobile,
  currentSwapPrice,
}) => {
  const [timeWindow, setTimeWindow] = useState<PairDataTimeWindowEnum>(0)

  const { data: pairPrices = [] } = useFetchPairPricesV3({
    token0Address,
    token1Address,
    timeWindow,
    currentSwapPrice,
  })

  const [hoverValue, setHoverValue] = useState<number | undefined>()
  const [hoverDate, setHoverDate] = useState<string | undefined>()
  const valueToDisplay = hoverValue || pairPrices[pairPrices.length - 1]?.value
  const { changePercentage, changeValue } = getTimeWindowChange(pairPrices)
  const isChangePositive = changeValue >= 0
  const chartHeight = isChartExpanded ? 'calc(100vh - 220px)' : '320px'
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()
  const currentDate = new Date().toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

  // Sometimes we might receive array full of zeros for obscure tokens while trying to derive data
  // In that case chart is not useful to users
  // const isBadData =
  //   pairPrices &&
  //   pairPrices.length > 0 &&
  //   pairPrices.every(
  //     (price) => !price.value || price.value === 0 || price.value === Infinity || Number.isNaN(price.value),
  //   )

  // if (isBadData) {
  //   return <NoChartAvailable token0Address={token0Address} token1Address={token1Address} isMobile={isMobile} />
  // }

  return (
    <>
      <Flex
        flexDirection={['row', null, null, null, null, null, 'row']}
        alignItems={['flex-start', null, null, null, null, null, 'center']}
        justifyContent="space-between"
        px={isMobile ? '16px' : '24px'}
        flexWrap="wrap"
      >
        <Flex flexDirection="column" pt="20px">
          <PairPriceDisplay
            value={pairPrices?.length > 0 && valueToDisplay}
            inputSymbol={inputCurrency?.symbol}
            outputSymbol={outputCurrency?.symbol}
          >
            <Text color={`${isChangePositive ? '#64AB2C' : ' #FC3660'}`} fontSize="16px" ml="4px" fontWeight={700}>
              {isNaN(changeValue) ? (
                <span
                  style={{
                    color: 'rgba(255,255,255,0.6)',
                  }}
                >
                  --
                </span>
              ) : (
                `${isChangePositive ? '+' : ''}${changeValue.toFixed(3)} (${changePercentage}%)`
              )}
            </Text>
          </PairPriceDisplay>
          <Text fontSize="14px" color="rgba(255, 255, 255, 0.6)" pt="2px">
            {hoverDate || currentDate}
          </Text>
        </Flex>
        <StyledButtonMenu>
          <ButtonMenu activeIndex={timeWindow} onItemClick={setTimeWindow} scale="sm">
            <ButtonMenuItemStyle>{t('24H')}</ButtonMenuItemStyle>
            <ButtonMenuItemStyle>{t('1W')}</ButtonMenuItemStyle>
            <ButtonMenuItemStyle>{t('1M')}</ButtonMenuItemStyle>
            <ButtonMenuItemStyle>{t('1Y')}</ButtonMenuItemStyle>
          </ButtonMenu>
        </StyledButtonMenu>
      </Flex>
      <Box
        backgroundColor="#1b2b45"
        height={isMobile ? '100%' : chartHeight}
        p={isMobile ? '0px' : '16px'}
        width="100%"
        borderRadius={16}
      >
        <SwapLineChart
          data={pairPrices}
          setHoverValue={setHoverValue}
          setHoverDate={setHoverDate}
          isChangePositive={isChangePositive}
          isChartExpanded={isChartExpanded}
          timeWindow={timeWindow}
        />
      </Box>
    </>
  )
}

export default memo(BasicChart, (prev, next) => {
  return (
    prev.token0Address === next.token0Address &&
    prev.token1Address === next.token1Address &&
    prev.isChartExpanded === next.isChartExpanded &&
    prev.isMobile === next.isMobile &&
    prev.isChartExpanded === next.isChartExpanded &&
    ((prev.currentSwapPrice !== null &&
      next.currentSwapPrice !== null &&
      prev.currentSwapPrice[prev.token0Address] === next.currentSwapPrice[next.token0Address] &&
      prev.currentSwapPrice[prev.token1Address] === next.currentSwapPrice[next.token1Address]) ||
      (prev.currentSwapPrice === null && next.currentSwapPrice === null))
  )
})
